import { Controller } from "@hotwired/stimulus";

interface AgGridOutlet {
  fetchQuestionData(url: string): void;
  exportCsv(questionName?: string): void;
  exportExcel(questionName?: string): void;
  confirmAndPost(url: string, options: { title?: string; message?: string; confirmLabel?: string }): Promise<boolean>;
}

const AG_GRID_ID = "application--toolkit--ag-grid-component--ag-grid-component";

export default class extends Controller {
  static targets = ["questionSelector", "confirmModal", "exportToggle", "exportDropdown"];
  static values = {
    questionDataUrl: String,
    obfuscationUrl: String,
    exportVoteByVoteUrl: String,
    exportAllCsvUrl: String,
    exportAllPdfUrl: String,
  };

  declare readonly questionSelectorTarget: HTMLSelectElement;
  declare readonly hasQuestionSelectorTarget: boolean;
  declare readonly confirmModalTarget: HTMLElement;
  declare readonly hasConfirmModalTarget: boolean;
  declare readonly exportToggleTarget: HTMLButtonElement;
  declare readonly hasExportToggleTarget: boolean;
  declare readonly exportDropdownTarget: HTMLElement;
  declare readonly hasExportDropdownTarget: boolean;
  declare readonly questionDataUrlValue: string;
  declare readonly obfuscationUrlValue: string;
  declare readonly exportVoteByVoteUrlValue: string;
  declare readonly exportAllCsvUrlValue: string;
  declare readonly exportAllPdfUrlValue: string;

  private outsideExportClickListener?: (event: MouseEvent) => void;

  connect() {
    // Mirrors AgGridComponent's toggleExportDropdown/closeExportDropdown pattern, but for a dropdown on the page itself.
    this.outsideExportClickListener = (event: MouseEvent) => {
      if (!this.hasExportDropdownTarget || this.exportDropdownTarget.hidden) return;
      const target = event.target as Node;
      if (this.hasExportToggleTarget && this.exportToggleTarget.contains(target)) return;
      if (this.exportDropdownTarget.contains(target)) return;
      this.closeExportDropdown();
    };
    document.addEventListener("click", this.outsideExportClickListener);
  }

  disconnect() {
    if (this.outsideExportClickListener) {
      document.removeEventListener("click", this.outsideExportClickListener);
    }
  }

  toggleExportDropdown() {
    if (!this.hasExportDropdownTarget) return;
    const isOpen = !this.exportDropdownTarget.hidden;
    if (isOpen) {
      this.closeExportDropdown();
    } else {
      this.exportDropdownTarget.hidden = false;
      if (this.hasExportToggleTarget) this.exportToggleTarget.setAttribute("aria-pressed", "true");
    }
  }

  private closeExportDropdown() {
    if (!this.hasExportDropdownTarget) return;
    this.exportDropdownTarget.hidden = true;
    if (this.hasExportToggleTarget) this.exportToggleTarget.setAttribute("aria-pressed", "false");
  }

  switchQuestion() {
    const grid = this.agGrid();
    if (!grid || !this.hasQuestionSelectorTarget || !this.questionDataUrlValue) return;
    grid.fetchQuestionData(this.buildQuestionDataUrl());
  }

  // Client-side, instant — reads the currently-selected question's label so the grid
  // controller can prepend it as a row above the exported headers.
  exportCsv() {
    this.agGrid()?.exportCsv(this.currentQuestionName());
  }

  exportXlsx() {
    this.agGrid()?.exportExcel(this.currentQuestionName());
  }

  // Server-side, async (delivered to Report Center) — mirrors legacy synchronous
  // "CSV - Vote by Vote" (all questions), but wrapped in a background worker.
  exportVoteByVote(event: Event) {
    event.preventDefault();
    void this.confirmAndExportAll(
      "CSV",
      this.exportVoteByVoteUrlValue,
      "This will export the vote-by-vote breakdown for all questions in a CSV file. Large exports may take a few minutes and will be delivered to your Report Center."
    );
  }

  // Server-side, async (delivered to Report Center) — gated behind a confirm modal
  // instead of the old bare browser `data-confirm` popup.
  exportAllCsv(event: Event) {
    event.preventDefault();
    void this.confirmAndExportAll(
      "CSV",
      this.exportAllCsvUrlValue,
      "This will export all vote-by-vote ballots in a CSV file. Large exports may take a few minutes and will be delivered to your Report Center."
    );
  }

  exportAllPdf(event: Event) {
    event.preventDefault();
    void this.confirmAndExportAll(
      "PDF",
      this.exportAllPdfUrlValue,
      "This will export all vote-by-vote ballots in a PDF file. Large exports may take a few minutes and will be delivered to your Report Center."
    );
  }

  private async confirmAndExportAll(format: "CSV" | "PDF", url: string, message: string) {
    const grid = this.agGrid();
    if (!grid || !url) return;
    await grid.confirmAndPost(url, {
      title: "Export All Results?",
      message,
      confirmLabel: "Export All",
    });
  }

  openConfirmModal() {
    if (!this.hasConfirmModalTarget) return;
    this.confirmModalTarget.removeAttribute("hidden");
    document.body.style.overflow = "hidden";
    const noButton = this.confirmModalTarget.querySelector<HTMLButtonElement>('[data-action*="closeConfirmModal"]');
    if (noButton) setTimeout(() => noButton.focus(), 50);
  }

  closeConfirmModal() {
    if (!this.hasConfirmModalTarget) return;
    this.confirmModalTarget.setAttribute("hidden", "");
    document.body.style.overflow = "";
  }

  backdropClickModal(event: Event) {
    if (event.target === this.confirmModalTarget) this.closeConfirmModal();
  }

  confirmToggleWeighting() {
    this.closeConfirmModal();
    if (!this.obfuscationUrlValue) return;
    const csrfToken = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
    fetch(this.obfuscationUrlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    }).then((response) => {
      if (response.ok) window.location.reload();
    }).catch((error) => {
      console.error("[VoteByVotesController] disable obfuscation error:", error);
    });
  }

  private agGrid(): AgGridOutlet | null {
    const el = this.element.querySelector(`[data-controller~="${AG_GRID_ID}"]`);
    if (!el) return null;
    return this.application.getControllerForElementAndIdentifier(el, AG_GRID_ID) as unknown as AgGridOutlet | null;
  }

  private buildQuestionDataUrl(): string {
    const params = new URLSearchParams();
    if (this.hasQuestionSelectorTarget) {
      params.set("question_id", this.questionSelectorTarget.value);
    }
    return `${this.questionDataUrlValue}?${params.toString()}`;
  }

  // The visible <option> text is truncated to 60 chars; the untruncated text is stashed on `data-full-label`.
  private currentQuestionName(): string | undefined {
    if (!this.hasQuestionSelectorTarget) return undefined;
    const option = this.questionSelectorTarget.selectedOptions[0];
    if (!option) return undefined;
    const label = (option.dataset.fullLabel ?? option.textContent ?? "").trim();
    return label || undefined;
  }
}
